import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { approvalsAPI, adjustmentsAPI, vehicleToWarehouseAPI, vehicleToVehicleAPI } from '../lib/api';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';
import { ClipboardCheck, Check, X, Loader2, Clock, CheckCircle, XCircle, Undo2, Settings2, Truck, Warehouse, ArrowRightLeft, Filter, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { DenyReasonModal } from '../components/modals/DenyReasonModal';
import { ListPagination } from '../components/ListPagination';

export const Approvals = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Phase 3.5: Server-side pagination - unified approvals list with URL sync
  const [approvals, setApprovals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  
  // Phase 3.5: URL-synced filter and pagination state
  const activeTab = searchParams.get('status') || 'pending';
  const requestType = searchParams.get('type') || 'all';
  const page = parseInt(searchParams.get('page')) || 1;
  const pageSize = parseInt(searchParams.get('per_page')) || 15;
  
  // Server response pagination data
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  
  // Helper to update URL params
  const updateParams = useCallback((updates) => {
    setSearchParams(prev => {
      const newParams = new URLSearchParams(prev);
      Object.entries(updates).forEach(([key, value]) => {
        // Clean up default values
        if (value === null || value === undefined || value === '' || value === 'all' || 
            (key === 'status' && value === 'pending') ||
            (key === 'page' && value === 1) || 
            (key === 'per_page' && value === 15)) {
          newParams.delete(key);
        } else {
          newParams.set(key, String(value));
        }
      });
      return newParams;
    }, { replace: true });
  }, [setSearchParams]);
  
  // URL-synced setters
  const setActiveTab = useCallback((value) => {
    updateParams({ status: value, page: 1 });
  }, [updateParams]);
  
  const setRequestType = useCallback((value) => {
    updateParams({ type: value, page: 1 });
  }, [updateParams]);
  
  const setPage = useCallback((value) => {
    updateParams({ page: value });
  }, [updateParams]);
  
  const setPageSize = useCallback((value) => {
    updateParams({ per_page: value, page: 1 });
  }, [updateParams]);
  
  // Request type labels for display
  const requestTypeLabels = {
    'all': 'All Requests',
    'adjustment': 'Adjustments',
    'vehicle_to_warehouse': 'Returns to Warehouse',
    'vehicle_to_vehicle': 'Vehicle Transfers'
  };
  
  // Deny modal state
  const [denyModal, setDenyModal] = useState({ open: false, requestId: null, type: null });

  // Phase 3.5: Fetch from unified endpoint with server-side pagination
  // Using AbortController to prevent race conditions and stale request errors
  useEffect(() => {
    const abortController = new AbortController();
    
    const fetchApprovals = async () => {
      setLoading(true);
      try {
        const params = {
          page,
          page_size: pageSize,
        };
        
        // Map activeTab to status
        if (activeTab !== 'pending') {
          params.status = activeTab; // 'pending', 'approved', 'denied'
        } else {
          params.status = 'pending';
        }
        
        // Map requestType to request_type
        if (requestType !== 'all') {
          params.request_type = requestType;
        }
        
        const response = await approvalsAPI.list(params, { signal: abortController.signal });
        
        // Only update state if request wasn't aborted
        if (!abortController.signal.aborted) {
          const data = response.data;
          setApprovals(data.items || []);
          setTotal(data.total || 0);
          setTotalPages(data.total_pages || 0);
          setPendingCount(data.pending_count || 0);
        }
      } catch (error) {
        // Don't show error toast for aborted requests (e.g., from rapid filter changes or unmount)
        if (error.name === 'CanceledError' || error.code === 'ERR_CANCELED' || abortController.signal.aborted) {
          return;
        }
        toast.error('Failed to load requests');
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false);
        }
      }
    };
    
    fetchApprovals();
    
    // Cleanup: abort request on dependency change or unmount
    return () => {
      abortController.abort();
    };
  }, [activeTab, requestType, page, pageSize]);
  
  // Refetch function for use after approve/deny actions
  const refetchApprovals = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page,
        page_size: pageSize,
        status: activeTab,
      };
      if (requestType !== 'all') {
        params.request_type = requestType;
      }
      const response = await approvalsAPI.list(params);
      const data = response.data;
      setApprovals(data.items || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 0);
      setPendingCount(data.pending_count || 0);
    } catch (error) {
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  }, [activeTab, requestType, page, pageSize]);
  
  // Phase 3.5: Pagination handlers
  const handlePageChange = (newPage) => {
    setPage(newPage);
  };
  
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
  };

  const handleApproveAdjustment = async (id) => {
    setProcessing(id);
    try {
      await adjustmentsAPI.approve(id);
      toast.success('Adjustment approved');
      refetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(null);
    }
  };

  const handleRejectAdjustment = async (id) => {
    setProcessing(id);
    try {
      await adjustmentsAPI.reject(id);
      toast.success('Adjustment rejected');
      refetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject');
    } finally {
      setProcessing(null);
    }
  };

  const handleApproveTransfer = async (id) => {
    setProcessing(id);
    try {
      await vehicleToWarehouseAPI.approve(id);
      toast.success('Return approved and inventory updated');
      refetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(null);
    }
  };

  const openDenyTransferModal = (id) => {
    setDenyModal({ open: true, requestId: id, type: 'transfer' });
  };

  const handleDenyTransfer = async (denialReason) => {
    const { requestId } = denyModal;
    setProcessing(requestId);
    try {
      await vehicleToWarehouseAPI.deny(requestId, denialReason);
      toast.success('Return request denied');
      setDenyModal({ open: false, requestId: null, type: null });
      refetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deny');
    } finally {
      setProcessing(null);
    }
  };

  // Vehicle-to-Vehicle Transfer handlers
  const handleApproveVehicleTransfer = async (id) => {
    setProcessing(id);
    try {
      await vehicleToVehicleAPI.approve(id);
      toast.success('Vehicle transfer approved and inventory updated');
      refetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve');
    } finally {
      setProcessing(null);
    }
  };

  const openDenyVehicleTransferModal = (id) => {
    setDenyModal({ open: true, requestId: id, type: 'vehicle-transfer' });
  };

  const handleDenyVehicleTransfer = async (denialReason) => {
    const { requestId } = denyModal;
    setProcessing(requestId);
    try {
      await vehicleToVehicleAPI.deny(requestId, denialReason);
      toast.success('Vehicle transfer request denied');
      setDenyModal({ open: false, requestId: null, type: null });
      refetchApprovals();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to deny');
    } finally {
      setProcessing(null);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return <Clock className="w-4 h-4 text-warning" />;
      case 'approved': return <CheckCircle className="w-4 h-4 text-success" />;
      case 'rejected': return <XCircle className="w-4 h-4 text-destructive" />;
      default: return null;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-warning/10 text-warning',
      approved: 'bg-success/10 text-success',
      rejected: 'bg-destructive/10 text-destructive'
    };
    return (
      <span className={`inline-flex items-center gap-1 text-xs font-bold px-2 py-1 rounded-full ${styles[status]}`}>
        {getStatusIcon(status)}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    if (type === 'adjustment') {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-blue-500/10 text-blue-600">
          <Settings2 className="w-3 h-3" />
          Adjustment
        </span>
      );
    }
    if (type === 'vehicle_to_vehicle') {
      return (
        <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-green-500/10 text-green-600">
          <ArrowRightLeft className="w-3 h-3" />
          Vehicle Transfer
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full bg-purple-500/10 text-purple-600">
        <Undo2 className="w-3 h-3" />
        Return to Warehouse
      </span>
    );
  };

  // Phase 3.5: Use server-side paginated approvals directly
  const requests = approvals;

  return (
    <Layout>
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground">Approvals</h1>
          <p className="text-muted-foreground mt-1 text-sm md:text-base">Review inventory adjustment and return requests</p>
        </div>

        {/* Desktop: Quick filter for request type (lg and up) */}
        <div className="hidden lg:flex flex-wrap gap-2">
          <Button
            variant={requestType === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setRequestType('all')}
            data-testid="filter-all"
          >
            All Requests
          </Button>
          <Button
            variant={requestType === 'adjustment' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setRequestType('adjustment')}
            data-testid="filter-adjustments"
          >
            <Settings2 className="w-4 h-4 mr-1" />
            Adjustments
          </Button>
          <Button
            variant={requestType === 'vehicle_to_warehouse' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setRequestType('vehicle_to_warehouse')}
            data-testid="filter-returns"
          >
            <Undo2 className="w-4 h-4 mr-1" />
            Returns to Warehouse
          </Button>
          <Button
            variant={requestType === 'vehicle_to_vehicle' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setRequestType('vehicle_to_vehicle')}
            data-testid="filter-vehicle-transfers"
          >
            <ArrowRightLeft className="w-4 h-4 mr-1" />
            Vehicle Transfers
          </Button>
        </div>

        {/* Mobile/Tablet: Request Type dropdown (below lg) */}
        <div className="lg:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" className="w-full justify-between" data-testid="request-type-dropdown">
                <span className="flex items-center gap-2">
                  <Filter className="w-4 h-4" />
                  <span>Request Type: {requestTypeLabels[requestType]}</span>
                </span>
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-[var(--radix-dropdown-menu-trigger-width)]">
              <DropdownMenuItem 
                onClick={() => setRequestType('all')}
                className={`cursor-pointer ${requestType === 'all' ? 'bg-accent' : ''}`}
              >
                All Requests
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setRequestType('adjustment')}
                className={`cursor-pointer ${requestType === 'adjustment' ? 'bg-accent' : ''}`}
              >
                <Settings2 className="w-4 h-4 mr-2" />
                Adjustments
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setRequestType('vehicle_to_warehouse')}
                className={`cursor-pointer ${requestType === 'vehicle_to_warehouse' ? 'bg-accent' : ''}`}
              >
                <Undo2 className="w-4 h-4 mr-2" />
                Returns to Warehouse
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => setRequestType('vehicle_to_vehicle')}
                className={`cursor-pointer ${requestType === 'vehicle_to_vehicle' ? 'bg-accent' : ''}`}
              >
                <ArrowRightLeft className="w-4 h-4 mr-2" />
                Vehicle Transfers
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {/* Status Tabs - horizontally scrollable on narrow screens */}
        <div className="overflow-x-auto -mx-4 px-4 lg:mx-0 lg:px-0">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="grid w-full grid-cols-3 min-w-[280px]">
              <TabsTrigger value="pending" data-testid="tab-pending">
                Pending {pendingCount > 0 && `(${pendingCount})`}
              </TabsTrigger>
              <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
              <TabsTrigger value="denied" data-testid="tab-rejected">Rejected</TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        {/* Results */}
        <div className="mt-6">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : requests.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="flex flex-col items-center justify-center py-12">
                  <ClipboardCheck className="w-12 h-12 text-muted-foreground mb-4" />
                  <p className="text-lg font-semibold text-foreground">No {activeTab} requests</p>
                  <p className="text-muted-foreground mt-1">
                    {activeTab === 'pending' ? 'All caught up!' : `No ${activeTab} requests yet`}
                  </p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {requests.map((req) => (
                  <Card key={`${req.request_type}-${req.id}`} data-testid={`request-${req.id}`}>
                    <CardContent className="p-4">
                      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            {getTypeBadge(req.request_type)}
                            {getStatusBadge(req.status)}
                            <span className="text-sm text-muted-foreground">
                              {format(new Date(req.created_at), 'MMM d, h:mm a')}
                            </span>
                          </div>
                          
                          {req.request_type === 'adjustment' ? (
                            // Adjustment Request Display
                            <>
                              <p className="font-semibold text-foreground">
                                {req.item_name} 
                                <span className={`ml-2 font-mono ${req.qty_delta > 0 ? 'text-success' : 'text-destructive'}`}>
                                  {req.qty_delta > 0 ? '+' : ''}{req.qty_delta}
                                </span>
                              </p>
                              <p className="text-sm text-muted-foreground">
                                {req.van_name} · Requested by {req.requester_name}
                              </p>
                              {req.note && (
                                <p className="text-sm text-foreground mt-2 bg-muted p-2 rounded italic">
                                  &ldquo;{req.note}&rdquo;
                                </p>
                              )}
                            </>
                          ) : req.request_type === 'vehicle_to_vehicle' ? (
                            // Vehicle-to-Vehicle Transfer Display
                            <>
                              <p className="font-semibold text-foreground">
                                {req.item_name}
                                <span className="ml-2 font-mono text-green-600">
                                  ×{req.quantity}
                                </span>
                              </p>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                                <Truck className="w-4 h-4" />
                                <span>{req.source_van_name}</span>
                                <span>→</span>
                                <Truck className="w-4 h-4" />
                                <span>{req.destination_van_name}</span>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                Requested by {req.requester_name}
                              </p>
                              {req.note && (
                                <p className="text-sm text-foreground mt-2 bg-muted p-2 rounded italic">
                                  &ldquo;{req.note}&rdquo;
                                </p>
                              )}
                              {req.denial_reason && req.status === 'denied' && (
                                <p className="text-sm text-destructive mt-2 bg-destructive/10 p-2 rounded">
                                  Denial reason: {req.denial_reason}
                                </p>
                              )}
                            </>
                          ) : (
                            // Vehicle-to-Warehouse Transfer Request Display
                            <>
                              <p className="font-semibold text-foreground">
                                {req.item_name}
                                <span className="ml-2 font-mono text-purple-600">
                                  ×{req.quantity}
                                </span>
                              </p>
                              <div className="flex items-center gap-2 text-sm text-muted-foreground mt-1">
                                <Truck className="w-4 h-4" />
                                <span>{req.source_van_name}</span>
                                <span>→</span>
                                <Warehouse className="w-4 h-4" />
                                <span>{req.destination_warehouse_name}</span>
                              </div>
                              <p className="text-sm text-muted-foreground">
                                Requested by {req.requester_name}
                              </p>
                              {req.note && (
                                <p className="text-sm text-foreground mt-2 bg-muted p-2 rounded italic">
                                  &ldquo;{req.note}&rdquo;
                                </p>
                              )}
                              {req.denial_reason && req.status === 'denied' && (
                                <p className="text-sm text-destructive mt-2 bg-destructive/10 p-2 rounded">
                                  Denial reason: {req.denial_reason}
                                </p>
                              )}
                            </>
                          )}
                          
                          {req.decided_at && req.approver_name && (
                            <p className="text-xs text-muted-foreground mt-2">
                              {req.status === 'approved' ? 'Approved' : 'Rejected'} by {req.approver_name} on {format(new Date(req.decided_at), 'MMM d, h:mm a')}
                            </p>
                          )}
                        </div>
                        
                        {req.status === 'pending' && (
                          <div className="flex gap-2 shrink-0">
                            {req.request_type === 'adjustment' ? (
                              <>
                                <Button
                                  variant="outline"
                                  className="border-destructive text-destructive hover:bg-destructive/10"
                                  onClick={() => handleRejectAdjustment(req.id)}
                                  disabled={processing === req.id}
                                  data-testid={`reject-${req.id}`}
                                >
                                  {processing === req.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4 mr-1" />}
                                  Reject
                                </Button>
                                <Button
                                  onClick={() => handleApproveAdjustment(req.id)}
                                  disabled={processing === req.id}
                                  data-testid={`approve-${req.id}`}
                                >
                                  {processing === req.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                                  Approve
                                </Button>
                              </>
                            ) : req.request_type === 'vehicle_to_vehicle' ? (
                              // Vehicle-to-Vehicle Transfer actions
                              <>
                                <Button
                                  variant="outline"
                                  className="border-destructive text-destructive hover:bg-destructive/10"
                                  onClick={() => openDenyVehicleTransferModal(req.id)}
                                  disabled={processing === req.id}
                                  data-testid={`deny-v2v-${req.id}`}
                                >
                                  {processing === req.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4 mr-1" />}
                                  Deny
                                </Button>
                                <Button
                                  onClick={() => handleApproveVehicleTransfer(req.id)}
                                  disabled={processing === req.id}
                                  data-testid={`approve-v2v-${req.id}`}
                                >
                                  {processing === req.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                                  Approve
                                </Button>
                              </>
                            ) : (
                              // Vehicle-to-Warehouse Transfer actions
                              <>
                                <Button
                                  variant="outline"
                                  className="border-destructive text-destructive hover:bg-destructive/10"
                                  onClick={() => openDenyTransferModal(req.id)}
                                  disabled={processing === req.id}
                                  data-testid={`deny-${req.id}`}
                                >
                                  {processing === req.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4 mr-1" />}
                                  Deny
                                </Button>
                                <Button
                                  onClick={() => handleApproveTransfer(req.id)}
                                  disabled={processing === req.id}
                                  data-testid={`approve-transfer-${req.id}`}
                                >
                                  {processing === req.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                                  Approve
                                </Button>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
                
                {/* Phase 3.5: Server-Side Pagination Controls */}
                {requests.length > 0 && (
                  <ListPagination
                    page={page}
                    pageSize={pageSize}
                    total={total}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    onPageSizeChange={handlePageSizeChange}
                    loading={loading}
                    pageSizeOptions={[10, 15, 25, 50]}
                  />
                )}
              </div>
            )}
        </div>
      </div>

      {/* Deny Reason Modal */}
      <DenyReasonModal
        open={denyModal.open}
        onClose={() => setDenyModal({ open: false, requestId: null, type: null })}
        onConfirm={denyModal.type === 'vehicle_to_vehicle' ? handleDenyVehicleTransfer : handleDenyTransfer}
        isLoading={processing === denyModal.requestId}
        title={denyModal.type === 'vehicle_to_vehicle' ? "Deny Vehicle Transfer" : "Deny Return Request"}
        description={denyModal.type === 'vehicle_to_vehicle' 
          ? "Please provide a reason for denying this vehicle transfer request (optional)."
          : "Please provide a reason for denying this return request (optional)."
        }
      />
    </Layout>
  );
};
